import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ViewEncapsulation, inject } from '@angular/core';
import { FormBuilder } from '@angular/forms';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';

import { SmartLockChooserComponent } from 'src/shared/components/smart-lock-chooser/smart-lock-chooser.component';

import { RemoteAccessAddress, SeamAccessCode, SeamService } from 'src/shared/services/seam.service';

import { TidySelectNumberValueModel } from 'src/models/tidy-select-item.model';

import { translations } from 'src/shared/constants/language-translations';

type DeviceOption = TidySelectNumberValueModel & { propertyName: string };

const HOUR_MS = 60 * 60 * 1000;
const DEFAULT_EXPIRATION_HOURS = 24;

// Values are hours; the back end falls back to 1 day when no end is sent.
const EXPIRATION_OPTIONS: TidySelectNumberValueModel[] = [
  { value: 1, viewValue: '1 hour' },
  { value: 4, viewValue: '4 hours' },
  { value: 12, viewValue: '12 hours' },
  { value: 24, viewValue: '1 day' },
  { value: 48, viewValue: '2 days' },
  { value: 72, viewValue: '3 days' },
  { value: 168, viewValue: '1 week' },
  { value: 336, viewValue: '2 weeks' }
];

const SHORT_DATE_TIME: Intl.DateTimeFormatOptions = {
  year: '2-digit',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit'
};

function appLanguage(): string {
  return localStorage.getItem('language') || 'en';
}

function translated(text: string, language: string): string {
  if (language === 'en') { return text; }
  return translations[text]?.[language] || text;
}

/** "Valid 10/29/26, 8:07 PM – 10/30/26, 8:07 PM" in the app's language and date format. */
export function formatValidRange(startsAt: string, endsAt: string): string {
  const language = appLanguage();
  const format = new Intl.DateTimeFormat(language === 'en' ? 'en-US' : language, SHORT_DATE_TIME);
  return `${translated('Valid', language)} ${format.format(new Date(startsAt))} – ${format.format(new Date(endsAt))}`;
}

@Component({
  selector: 'smartlock-test-code',
  templateUrl: './smartlock-test-code.component.html',
  styleUrls: ['./smartlock-test-code.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class SmartlockTestCodeComponent implements OnInit {
  private rightSidePanelService = inject(RightSidePanelService);
  private navCtrl = inject(CustomNavController);
  private cdr = inject(ChangeDetectorRef);
  private storage = inject(TidyStorage);
  private seamService = inject(SeamService);
  private util = inject(Util);
  private formBuilder = inject(FormBuilder);
  windowService = inject(WindowService);

  state: 'loading' | 'ready' | 'generating' | 'result' | 'error' | 'empty' = 'loading';
  deviceOptions: DeviceOption[] = [];
  readonly expirationOptions = EXPIRATION_OPTIONS;
  deviceForm = this.formBuilder.group({
    deviceId: this.formBuilder.control<number | null>(null),
    expirationHours: this.formBuilder.control<number>(DEFAULT_EXPIRATION_HOURS)
  });
  selectedDeviceLabel = '';
  generatedCode: SeamAccessCode | null = null;
  validRangeText = '';
  errorMessage = '';
  private errorSource: 'load' | 'generate' | null = null;
  private lastPreselectedDeviceId: number | null = null;

  async ngOnInit() {
    this.rightSidePanelService.setDialogPageTitle('Generate Access Code');
    const params = await this.storage.retrieve('dialog-params');
    const preselectedDeviceId: number | null = params?.preselectedDeviceId ?? null;
    this.lastPreselectedDeviceId = preselectedDeviceId;
    await this.loadDevices(preselectedDeviceId);
  }

  private async loadDevices(preselectedDeviceId: number | null) {
    this.state = 'loading';
    this.errorMessage = '';
    this.errorSource = null;
    this.cdr.markForCheck();
    try {
      const addresses = await this.seamService.getRemoteAccessAddresses();
      this.deviceOptions = this.buildDeviceOptions(addresses);
      if (this.deviceOptions.length === 0) {
        this.state = 'empty';
        this.cdr.markForCheck();
        return;
      }
      const match = preselectedDeviceId
        ? this.deviceOptions.find(opt => opt.value === preselectedDeviceId)
        : null;
      if (match) {
        this.deviceForm.patchValue({ deviceId: match.value });
        this.selectedDeviceLabel = match.viewValue;
      } else {
        const first = this.deviceOptions[0];
        this.deviceForm.patchValue({ deviceId: first.value });
        this.selectedDeviceLabel = first.viewValue;
      }
      this.state = 'ready';
      this.cdr.markForCheck();
    } catch (err: any) {
      this.state = 'error';
      this.errorSource = 'load';
      this.errorMessage = this.extractErrorMessage(err, 'Unable to load your smart locks. Please try again.');
      this.cdr.markForCheck();
    }
  }

  private extractErrorMessage(err: any, fallback: string): string {
    if (err?.status === 403) {
      return "You don't have permission to manage this smart lock's address.";
    }
    return err?.error?.error?.message
      || err?.error?.message
      || err?.message
      || fallback;
  }

  private buildDeviceOptions(addresses: RemoteAccessAddress[]): DeviceOption[] {
    const options: DeviceOption[] = [];
    for (const raa of addresses) {
      const names = (raa.addresses ?? [])
        .map(a => a.address_name || a.address1)
        .filter((name): name is string => !!name);
      const propertyName = names.length > 0
        ? names.join(', ')
        : (raa.name || translated('Unassigned', appLanguage()));
      for (const device of raa.remote_access_devices) {
        options.push({
          value: device.id,
          viewValue: `${device.device_name} (${propertyName})`,
          propertyName
        });
      }
    }
    return options;
  }

  onDeviceChange = () => {
    const selectedId = this.deviceForm.controls.deviceId.value;
    const match = this.deviceOptions.find(opt => opt.value === Number(selectedId));
    this.selectedDeviceLabel = match?.viewValue || '';
    this.cdr.markForCheck();
  };

  generateCode = async () => {
    const deviceId = Number(this.deviceForm.controls.deviceId.value);
    if (!deviceId) {
      this.util.showError('Please select a smart lock.');
      return;
    }
    this.state = 'generating';
    this.errorMessage = '';
    this.errorSource = null;
    this.cdr.markForCheck();
    const hours = Number(this.deviceForm.controls.expirationHours.value) || DEFAULT_EXPIRATION_HOURS;
    const startsAt = new Date();
    const endsAt = new Date(startsAt.getTime() + hours * HOUR_MS);
    try {
      this.generatedCode = await this.seamService.generateAccessCode(deviceId, {
        starts_at: startsAt.toISOString(),
        ends_at: endsAt.toISOString()
      });
      this.validRangeText = this.generatedCode?.starts_at && this.generatedCode?.ends_at
        ? formatValidRange(this.generatedCode.starts_at, this.generatedCode.ends_at)
        : '';
      this.state = 'result';
      this.cdr.markForCheck();
    } catch (err: any) {
      this.errorMessage = this.extractErrorMessage(err, 'Unable to generate a code. Please try again.');
      this.state = 'error';
      this.errorSource = 'generate';
      this.cdr.markForCheck();
    }
  };

  retry = async () => {
    if (this.errorSource === 'load') {
      await this.loadDevices(this.lastPreselectedDeviceId);
    } else {
      await this.generateCode();
    }
  };

  copyCode = () => {
    if (!this.generatedCode?.code) {return;}
    this.util.copyToClipBoard(this.generatedCode.code);
    this.util.showSuccess('Code copied to clipboard.');
  };

  goToConnectLocks = async () => {
    if (this.windowService.isDesktopRes) {
      const desktopParams = {};
      await this.storage.save('dialog-params', desktopParams);
      await this.rightSidePanelService.navigateTo(
        'smart-locks/chooser',
        desktopParams,
        SmartLockChooserComponent
      );
    } else {
      this.navCtrl.navigateForward('smart-locks/chooser');
    }
  };
}
